<template>
  <div v-if="question" class="plv-ia-qn-edit-question">
    <div class="plv-ia-qn-edit-question__control plv-ia-clearfix">
      <label>{{ t('plvIA.questionnaire.questionType') }}</label>
      <div class="plv-ia-qn-edit-question__control__right plv-ia-clearfix">
        <FormSelect
          v-model="question.type"
          :options-data="TYPE_TEXT"
          default-index="0"
        />
        <span class="plv-ia-qn-edit-question__control__item">
          <label>{{ t('plvIA.questionnaire.required') }}</label>
          <input v-model="question.isRequired" type="checkbox" />
        </span>
        <span v-show="question.type !== 'Q'" class="plv-ia-qn-edit-question__control__item">
          <label>{{ t('plvIA.questionnaire.scoringQuestions') }}</label>
          <input v-model="question.isScorable" type="checkbox" />
          <span v-show="question.isScorable">
            <input
              ref="score"
              v-model.lazy="question.score"
              v-limitScore
              type="text"
              max-length="3"
            />{{ t('plvIA.questionnaire.score') }}
          </span>
        </span>

        <span class="plv-ia-qn-edit-question__control__icons">
          <i v-show="questionIndex !== 0" class="plv-ia-qn-icon plv-ia-qn-icon-up" @click="handleEditQuestion('up')"></i>
          <i v-show="!isLast" class="plv-ia-qn-icon plv-ia-qn-icon-down" @click="handleEditQuestion('down')"></i>
          <i class="plv-ia-qn-icon plv-ia-qn-icon-delete" @click="handleEditQuestion('delete')"></i>
        </span>
      </div>
    </div>

    <div class="plv-ia-qn-edit-question__title pws-vclass-qn-edit-question__title plv-ia-clearfix">
      <label>{{ questionIndex + 1 }}.</label>
      <div>
        <div class="plv-ia-qn-edit-question__title__wrap">
          <textarea
            ref="name"
            v-model.trim="question.name"
            :placeholder="t('plvIA.questionnaire.enterTitle')"
            :maxlength="maxLen.questionTitle"
          ></textarea>
          <span><span :class="{ 'plv-ia-err': strLen(question.name || '') > maxLen.questionTitle }">{{ strLen(question.name || '') }}</span>/{{ maxLen.questionTitle }}</span>
        </div>
      </div>
    </div>

    <div v-if="question.type !== 'Q'" ref="option-list" class="plv-ia-qn-edit-option-list">
      <div
        v-for="(item, index) in question.options"
        :key="`question${questionIndex + 1}_option${index + 1}`"
        class="plv-ia-qn-edit-option"
      >
        <div v-if="question.type === 'R'" class="plv-ia-qn-edit__input pws-vclass-qn-edit__input">
          <div class="plv-ia-qn-edit__input__wrap">
            <FormRadio
              v-model="question.answer"
              :name="`answer_${questionIndex + 1}`"
              :value="index | answerCode"
              :disabled="!question.isScorable"
            >
              <input
                ref="option"
                v-model.trim="question.options[index]"
                type="text"
                data-type="option"
                :maxlength="maxLen.option"
                :placeholder="t('plvIA.questionnaire.enterOptions')"
              />
              <span class="plv-ia-qn-edit__input-count"><span :class="{ 'plv-ia-err': strLen(question.options[index]) > maxLen.option }">{{ strLen(question.options[index]) }}</span>/{{ maxLen.option }}</span>
            </FormRadio>
          </div>
        </div>

        <div v-if="question.type === 'C'" class="plv-ia-qn-edit__input">
          <div class="plv-ia-qn-edit__input__wrap">
            <FormCheckbox
              v-model="question.answer"
              name="answer"
              :value="index | answerCode"
              :disabled="!question.isScorable"
            >
              <input
                v-model="question.options[index]"
                type="text"
                data-type="option"
                :maxlength="maxLen.option"
                :placeholder="t('plvIA.questionnaire.enterOptions')"
              />
              <span class="plv-ia-qn-edit__input-count">{{ question.options[index].length }}/{{ maxLen.option }}</span>
            </FormCheckbox>
          </div>
        </div>

        <div class="plv-ia-qn-edit-option__op">
          <i
            v-show="calculateDelVisible(index)"
            class="plv-ia-qn-edit-option__op__item plv-ia-qn-edit-option__op__item--del"
            @click="deleteOption(index)"
          ></i>
          <i
            v-show="calculateAddVisible(index)"
            class="plv-ia-qn-edit-option__op__item plv-ia-qn-edit-option__op__item--add"
            @click="addOption(index)"
          ></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneJSON } from '@polyv/utils/src/lang';
import { strLen } from '@polyv/utils/src/string';
import FormSelect from '@/components/form/Select';
import FormRadio from '@/components/form/Radio';
import FormCheckbox from '@/components/form/Checkbox';
import { maxOptionsCount, minOptionsCount, maxLen, toLowercaseLetter } from '@/assets/app/questionnaire/helper';
import Locale from '@/mixins/locale';

export default {
  components: {
    FormSelect,
    FormRadio,
    FormCheckbox,
  },

  directives: {
    // 限制输入框内容为最多3位的正整数
    // 注意：1.type类型必须为text 2.返回字符串类型
    limitScore: {
      inserted(el) {
        el.addEventListener('keyup', () => {
          el.value = el.value.replace(/[^\d]/g, '').slice(0, 3);
        });
      }
    }
  },

  filters: {
    answerCode(index) {
      return String.fromCharCode(97 + index);
    }
  },

  mixins: [Locale],

  props: {
    questionData: {
      type: Object,
      default() {
        return {};
      }
    },
    questionIndex: {
      type: Number,
      default: -1,
    },
    isLast: Boolean
  },

  data() {
    return {
      TYPE_TEXT: {
        R: this.t('plvIA.questionnaire.oneChoiceLabel'),
        C: this.t('plvIA.questionnaire.multipleChoiceLabel'),
        Q: this.t('plvIA.questionnaire.qaLabel'),
      },
      maxLen,
      question: null,
    };
  },

  watch: {
    questionData: {
      immediate: true,
      handler(questionData) {
        this.question = cloneJSON(questionData);
        if (this.question.type === 'C') {
          this.question.answer = this.question.answer.split('');
        }
      },
    },

    'question.type': {
      handler() {
        this.question.answer = this.question.type === 'C' ? [] : '';
        if (this.question.type === 'Q') {
          this.question.isScorable = false;
        }
      }
    },

    'question.isScorable': {
      handler() {
        this.question.answer = this.question.type === 'C' ? [] : '';
      }
    },
  },

  methods: {
    strLen,

    addOption(optionIndex) {
      this.question.options.splice(optionIndex + 1, 0, '');
      this.$nextTick(() => {
        const optionInputList = Array.prototype.filter.call(this.$refs['option-list'].getElementsByTagName('input'), (ele) => ele.type === 'text');
        optionInputList[optionIndex + 1].focus();
      });
    },

    deleteOption(optionIndex) {
      this.question.options.splice(optionIndex, 1);
      const isMultiple = this.question.type === 'C';
      let answer = isMultiple ? this.question.answer.join('') : this.question.answer;
      const letter = toLowercaseLetter(optionIndex);
      answer = answer.replace(letter, '');
      this.question.answer = isMultiple ? answer.split('') : answer;
    },

    // 计算“添加”按钮是否可见
    calculateAddVisible(index) {
      return index + 1 === this.question.options.length && index + 1 < maxOptionsCount;
    },

    // 计算“删除”按钮是否可见
    calculateDelVisible() {
      return this.question.options.length > minOptionsCount;
    },

    // 操作题目（上移/下移/删除）
    handleEditQuestion(opType) {
      this.$emit('edit', {
        type: opType,
        index: this.questionIndex,
      });
    },
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/mixins.scss';

$imgPrefix: './images/';

.plv-ia-qn-edit-question {
  position: relative;
  margin: 20px 0;
}

.plv-ia-qn-edit-question__control {
  line-height: 40px;

  >* {
    float: left;
  }

  >label {
    margin-right: 8px;
    width: 60px;
    text-align: right;
  }

  .plv-ia-qn-icon {
    height: 36px;
    cursor: pointer;
  }
}

.plv-ia-qn-edit-question__control__right {
  position: relative;

  >* {
    float: left;
  }

  .plv-ia-select {
    width: 126px;
  }

  input[type=text] {
    width: 40px;
    height: 36px;
    background: rgba(255, 255, 255, 1);
    border: 1px solid rgba(224, 224, 224, 1);
    margin-left: 15px;
    margin-right: 6px;
    padding: 0 5px;
    box-sizing: border-box;
    vertical-align: top;
  }

  input[type=checkbox]:not(:checked)~span {
    display: none;
  }

  input[type=checkbox] {
    -webkit-appearance: none;
    height: 20px;
    width: 32px;
    background: #E0E0E0;
    border-radius: 10px;
    vertical-align: middle;
    position: relative;
    outline: none;
    cursor: pointer;

    &:before {
      content: '';
      width: 16px;
      height: 16px;
      background: #fff;
      border-radius: 50%;
      position: absolute;
      top: 2px;
      left: 2px;
      transition: all .3s;
    }

    &:checked {
      background: #2096F3;

      &:before {
        top: 2px;
        right: 2px;
        left: auto;
      }
    }
  }
}

.plv-ia-qn-edit-question__control__item {
  margin-left: 8px;
  font-size: 0;

  >* {
    font-size: 14px;
    vertical-align: top;
  }

  >label {
    margin-right: 8px;
  }
}

.plv-ia-qn-edit-question__control__item + .plv-ia-qn-edit-question__control__item {
  margin-left: 16px;
}

.plv-ia-qn-edit-question__control__icons {
  position: absolute;
  left: 400px;
  white-space: nowrap;
  font-size: 0;
  margin-left: 4px;
}

.plv-ia-qn-icon {
  display: inline-block;
  width: 16px;
  height: 40px;
  line-height: 40px;
  background-repeat: no-repeat;
  background-position: left;
  background-size: 16px;
  padding: 0 4px; // 扩大可点击范围

  &-up {
    background-image: url($imgPrefix + 'icon-up.png');

    &:hover {
      background-image: url($imgPrefix + 'icon-up-hover.png');
    }
  }

  &-down {
    background-image: url($imgPrefix + 'icon-down.png');

    &:hover {
      background-image: url($imgPrefix + 'icon-down-hover.png');
    }
  }

  &-delete {
    background-image: url($imgPrefix + 'icon-del.png');

    &:hover {
      background-image: url($imgPrefix + 'icon-del-hover.png');
    }

    &:active {
      background-image: url($imgPrefix + 'icon-del-active.png');
    }
  }
}

.plv-ia-qn-edit-question__title {
  margin: 15px 0;
  font-size: 0;

  >* {
    font-size: 14px;
    float: left;
  }

  >label {
    width: $questionaire-label-left;
    text-align: right;
    margin-right: 8px;
  }

  >div {
    @include input();
    height: 80px;
    width: 400px;
    padding: 0 2px;
  }
}

.plv-ia-qn-edit-question__title__wrap {
  padding-bottom: 22px;
  height: 100%;
  box-sizing: border-box;

  >span {
    line-height: 22px;
    right: 8px;
    position: absolute;
    bottom: 0;
    font-size: 12px;
    color: #9e9e9e;
    user-select: none;
  }
}

.plv-ia-qn-edit-option-list {
  padding-left: $questionaire-label-left + 8px;
}

.plv-ia-qn-edit-option {
  margin-bottom: 20px;
  position: relative;
}

.plv-ia-qn-edit__input {
  @include input();
  width: 400px;

  .plv-ia-radio, .plv-ia-checkbox {
    >input:disabled+label {
      opacity: 1;
    }
  }

  .plv-ia-radio, .plv-ia-checkbox {
    height: 100%;
    width: 100%;
    margin: 0;
    vertical-align: top;
    position: relative;
    user-select: none;
  }

  .plv-ia-radio >input+label {
    width: 100%;
    display: block;
    box-sizing: border-box;
    line-height: 38px;
    height: 100%;
  }

  .plv-ia-checkbox>label {
    line-height: 38px;
    height: 100%;
    width: 100%;
    display: block;
    box-sizing: border-box;
  }
}

.plv-ia-qn-edit__input__wrap {
  position: relative;
}

.plv-ia-qn-edit__input-count {
  position: absolute;
  bottom: 0;
  right: 0;
  line-height: 38px;
  font-size: 12px;
  color: #9E9E9E;
  user-select: none;
}

.plv-ia-qn-edit-option__op {
  position: absolute;
  left: 400px;
  top: 0;
  line-height: 40px;
  height: 40px;
  font-size: 0;
  margin-left: 4px;
}

.plv-ia-qn-edit-option__op__item {
  display: inline-block;
  width: 16px;
  height: 40px;
  background-origin: content-box;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  vertical-align: top;
  cursor: pointer;
  padding: 0 4px; // 扩大可点击范围

  &--del {
    background-image: url(./images/icon-del.png);

    &:hover {
      background-image: url(./images/icon-del-hover.png);
    }

    &:active {
      background-image: url(./images/icon-del-active.png);
    }
  }

  &--add {
    background-image: url(./images/icon-add.png);

    &:hover {
      background-image: url(./images/icon-add-hover.png);
    }

    &:active {
      background-image: url(./images/icon-add-active.png);
    }
  }
}
</style>
